<template>
  <div>
    <NuxtPwaAssets />

    <a
      href="#contenido-principal"
      class="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-white focus:text-black focus:px-16 focus:py-8 focus:rounded-sm"
    >
      Saltar al contenido
    </a>

    <!-- Top ad — isolated from <main> to prevent AdSense style injection -->
    <div
      :class="[
        'ad-slot-reservation ad-slot-reservation--top',
        !showTopAdSlot && 'ad-slot-reservation--collapsed',
      ]"
    >
      <div class="ad-slot-wrapper ad-slot-wrapper--top">
        <ClientOnly>
          <LazyVisibleMbAdsense
            v-if="adSenseReady && showTopAdSlot"
            hydrate-on-visible
            class="adsbygoogle"
            style="display: block"
            ad-slot="4357125401"
            ad-format="auto"
            full-width-responsive="true"
          />
          <template #fallback>
            <div class="ad-slot-placeholder" />
          </template>
        </ClientOnly>
      </div>
    </div>

    <main id="contenido-principal" class="mb-main__container container mx-auto">
      <OfflineIndicator />
      <NotificacionesPanel />

      <NuxtPage class="mb-view" />

      <ClientOnly>
        <PWAInstall />
      </ClientOnly>

      <nav
        role="navigation"
        class="mb-nav__container fixed bottom-0 left-0 w-full border-t border-gray-300 grid grid-cols-2"
        v-show="route.fullPath !== '/' && !route.fullPath.startsWith('/#')"
      >
        <NuxtLink
          :to="`/${servicio}${route.params.idsalida ? `/${route.params.idsalida}` : ''}${
            route.params.idllegada ? `/${route.params.idllegada}` : ''
          }`"
          aria-label="Ir a los horarios del biotren"
          title="Ir a los horarios del biotren"
          :class="
            !['biotren', 'tren-araucania'].includes(String(servicio)) &&
            'col-span-2'
          "
        >
          <LucideMapPin
            :size="18"
            class="mb-4"
            aria-hidden="true"
            :class="[
              !route.path.includes('/mapa') &&
                !route.path.includes('/saldo') &&
                'text-orange!',
            ]"
          />
          <span class="hidden">Ir a los horarios del biotren</span>
          <small class="uppercase text-xs text-center block font-bold"
            >Horarios</small
          >
        </NuxtLink>

        <NuxtLink
          v-if="['biotren', 'tren-araucania'].includes(String(servicio))"
          :to="`/${servicio}/mapa`"
          aria-label="Ir a los mapas de estaciones"
          title="Ir a los mapas de estaciones"
          class="text-gray-800"
          active-class="text-orange-800"
          prefetch-on="interaction"
        >
          <LucideMap
            :size="18"
            aria-hidden="true"
            class="mb-4"
            :class="[route.path.includes('/mapa') && 'text-orange!']"
          />
          <span class="hidden">Ir a los mapas de estaciones</span>
          <small class="uppercase text-xs text-center block font-bold"
            >Mapa</small
          >
        </NuxtLink>
      </nav>
    </main>

    <!-- Bottom ad — placed outside <main> to isolate from content -->
    <div
      v-if="!route.path.includes('mapa') && route.fullPath !== '/'"
      class="ad-slot-wrapper ad-slot-wrapper--bottom"
    >
      <ClientOnly>
        <LazyMbAdsense
          v-if="adSenseReady"
          class="adsbygoogle"
          style="display: block"
          ad-slot="4357125401"
          ad-format="auto"
          full-width-responsive="true"
        />
        <template #fallback>
          <div class="ad-slot-placeholder" />
        </template>
      </ClientOnly>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useWebWorkerFn } from '@vueuse/core';

import { useHorarioStore } from '~~/stores/horarios';
import { useNotificacionesStore } from '~~/stores/notificaciones';
import { useUserStore } from '~~/stores/userconfig';
import { useServiciosStore } from '~~/stores/servicios';

import '~/assets/css/tailwind.css';
import {
  ALLOWED_SERVICES_PAIRS,
  BASE_SERVICIO_ID,
  type TServiciosSlug,
} from '~~/constants';

const config = useRuntimeConfig();
const route = useRoute();
const router = useRouter();
const userConf = useUserStore();
const colorMode = useColorMode();
const servicios = useServiciosStore();
const { $pwa } = useNuxtApp();

colorMode.preference = userConf.theme;
const ADSENSE_TIMEOUT = 500;

const servicio = computed(
  () =>
    (router.currentRoute.value.params.servicio as TServiciosSlug) || 'biotren'
);

const horarios = useHorarioStore();
const notifs = useNotificacionesStore();

const controller = new AbortController();
const adSenseReady = ref(false);
const showTopAdSlot = computed(
  () =>
    !route.path.includes('mapa') &&
    route.fullPath !== '/' &&
    !route.fullPath.startsWith('/#')
);

const loadAdSense = () => {
  if (adSenseReady.value) return;
  adSenseReady.value = true;
  useScript(
    {
      src: 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js',
      async: true,
      defer: true,
      crossorigin: 'anonymous',
    },
    {
      use: () => ({
        client: config.public.adsense || 'ca-pub-8104818500522443',
        'data-add-client': config.public.adsense || 'ca-pub-8104818500522443',
      }),
    }
  );
};

const { workerFn } = useWebWorkerFn(
  async ({
    db,
    name,
    servicio,
  }: {
    db: string;
    name: string;
    servicio: string;
  }) => {
    try {
      const broadcast = new BroadcastChannel('PERCENT');

      const Horarios = new PouchDB(name);
      const HorariosRemote = new PouchDB(`${db}/${name}`);
      const Servicios = new PouchDB('servicios');
      const ServiciosRemote = new PouchDB(`${db}/servicios`);

      const sendMessage =
        (prefix = '') =>
        (message: string | number, type = 'MESSAGE') =>
          broadcast.postMessage({
            type: `${prefix ? `${prefix}:` : ''}${type}`,
            message,
          });

      // PouchDB is not working on Apple iOS devices on Private Tabs. We check if replication works, and if not we disable it.
      const isReplicationActive: null | ReturnType<typeof setTimeout> =
        setTimeout(() => {
          sendMessage('REPL')('', 'OFF');
        }, 5e3);

      const promiseReplicate = (
        localDB: PouchDB.Database<{}>,
        remoteDB: PouchDB.Database<{}>,
        sendMessage: (message: string | number, type?: string) => void
      ) => {
        return new Promise((res, rej) => {
          localDB.replicate
            .from(remoteDB, {
              retry: true,
              filter: 'filters/byPrefix',
              query_params: { prefix: `${servicio}>` },
            })
            .on('change', (info) => {
              // @ts-ignore
              const percent = (info.docs_written / info.pending) * 100;
              sendMessage(percent, 'PERCENT');

              if (isReplicationActive) {
                clearTimeout(isReplicationActive);
              }
            })
            .on('complete', (data) => {
              sendMessage(100, 'PERCENT');
              sendMessage('COMPLETE');
              if (isReplicationActive) {
                clearTimeout(isReplicationActive);
              }
              res(data);
            })
            .on('error', (err) => {
              console.log({ err, message: 'ERROR' });
              sendMessage('ERROR', JSON.stringify(err, null, 2));
              if (isReplicationActive) {
                clearTimeout(isReplicationActive);
              }
              res(null);
            });
        });
      };

      const syncServicios = () =>
        promiseReplicate(Servicios, ServiciosRemote, sendMessage('SRV'));

      const syncHorarios = async () => {
        await promiseReplicate(Horarios, HorariosRemote, sendMessage('HOR'));
      };

      await Promise.all([syncServicios(), syncHorarios()]);
    } catch (e) {
      console.log('ERROR syncing pouchdb', e);
    }
  },
  {
    dependencies: [
      'https://cdnjs.cloudflare.com/ajax/libs/pouchdb/9.0.0/pouchdb.min.js',
    ],
  }
);

// Phase 1: do NOT block hydration — fetch in background
useAsyncData(servicio.value, () => {
  return servicios.populateServicios(true).then(() => servicios.checkFeriado());
});

onMounted(() => {
  const broadcast = new BroadcastChannel('PERCENT');
  // const broadcastNotifs = new BroadcastChannel('NOTIFS');
  const servicioId = ALLOWED_SERVICES_PAIRS.get(servicio.value);
  notifs.getNotificaciones(servicioId);

  broadcast.addEventListener(
    'message',
    (event: MessageEvent<{ type: string; message: string }>) => {
      // @ts-ignore
      if (event.target?.name === 'PERCENT') {
        if (event.data.type === 'REPL:OFF') {
          horarios.couldSyncHorarios(false);
          horarios.completedFirstRun();
        }
        if (event.data.type === 'HOR:PERCENT') {
          horarios.updateFn(parseInt(event.data.message, 10));
        }

        if (event.data.type === 'HOR:MESSAGE') {
          if (event.data.message === 'COMPLETE') {
            horarios.couldSyncHorarios(true);
            horarios.completedFirstRun();
          }

          if (event.data.message === 'ERROR') {
            console.log('ERROR', event.data);
            horarios.couldSyncHorarios(false);
            horarios.completedFirstRun();
          }
        }
      }
      if (event.data?.type === 'NOTIFS') {
        // notifs.updateNotificaciones(event.data.notificaciones);
      }
    },
    { signal: controller.signal }
  );

  if ($pwa?.needRefresh) {
    $pwa.updateServiceWorker();
  }

  setTimeout(loadAdSense, ADSENSE_TIMEOUT);

  workerFn({
    db: 'https://db.mibiotren.cl',
    name: 'horariosv4',
    servicio: servicioId || BASE_SERVICIO_ID,
  });

  watch(
    () => router.currentRoute.value.params.servicio || BASE_SERVICIO_ID,
    () => {
      workerFn({
        db: 'https://db.mibiotren.cl',
        name: 'horariosv4',
        servicio: servicioId || BASE_SERVICIO_ID,
      });
    }
  );
});

onBeforeUnmount(() => {
  controller.abort();
});
</script>

<style>
@reference './assets/css/tailwind.css';
html {
  scrollbar-gutter: stable;
}
#app {
  max-width: 100%;
  overflow-x: hidden;
}
.mb-view {
  max-width: 800px;
  margin: 0 auto;
  position: relative;
}

.monotype {
  font-family: 'Roboto Mono', 'Roboto Mono Fallback', monospace;
}
.dark body {
  color: white;
  background-color: var(--color-azul);
}
.incline {
  transform: rotate(-12deg);
}
.mb-main__container {
  padding-bottom: 56px;
}

.mb-nav__container a {
  transition: all 0.25s ease-in-out;
  padding: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  @apply light:text-gray-700 dark:text-gray-200 light:bg-white dark:bg-azul;
}

.mb-nav__container a.router-link-exact-active svg {
  @apply text-orange-500;
}
.dark .mb-nav__container {
  background-color: var(--color-azul);
  border-color: var(--color-gray-700);
}
.dark .mb-nav__container svg {
  color: var(--color-gray-300);
}

.dark .mb-nav__container .router-link-exact-active svg {
  @apply text-orange;
}

@keyframes breathing {
  0% {
    -webkit-transform: scale(0.9);
    -ms-transform: scale(0.9);
    transform: scale(0.9);
  }

  25% {
    -webkit-transform: scale(1);
    -ms-transform: scale(1);
    transform: scale(1);
  }

  60% {
    -webkit-transform: scale(0.9);
    -ms-transform: scale(0.9);
    transform: scale(0.9);
  }

  100% {
    -webkit-transform: scale(0.9);
    -ms-transform: scale(0.9);
    transform: scale(0.9);
  }
}

@keyframes rotation {
  0% {
    transform: rotate(0);
  }
  100% {
    transform: rotate(360deg);
  }
}

.rotation {
  animation: rotation 3s linear infinite normal;
}

.buscando svg {
  animation: breathing 5s ease-out infinite normal;
  @apply text-orange;
}

/* AdSense isolation: fixed-height containers prevent layout shift */
.ad-slot-reservation {
  contain: layout;
  height: 280px;
  min-height: 280px;
  max-height: 280px;
  overflow: hidden;
}

.ad-slot-reservation--collapsed {
  height: 0 !important;
  min-height: 0 !important;
  max-height: 0 !important;
}

.ad-slot-reservation--collapsed .ad-slot-placeholder {
  display: none;
}

.ad-slot-wrapper {
  contain: layout;
  content-visibility: auto;
  contain-intrinsic-size: auto 280px;
  height: 280px;
  min-height: 280px;
  max-height: 280px;
  overflow: hidden;
}

.ad-slot-wrapper .adsbygoogle {
  display: block;
  width: 100%;
  height: 100%;
  min-height: inherit;
  max-height: inherit;
}

.ad-slot-wrapper--top {
  content-visibility: visible;
}

.ad-slot-wrapper--collapsed {
  height: 0 !important;
  min-height: 0 !important;
  max-height: 0 !important;
}

.ad-slot-wrapper--collapsed .ad-slot-placeholder {
  display: none;
}

@media (min-width: 640px) {
  .ad-slot-reservation {
    height: 90px;
    min-height: 90px;
    max-height: 90px;
  }

  .ad-slot-wrapper {
    contain-intrinsic-size: auto 90px;
    height: 90px;
    min-height: 90px;
    max-height: 90px;
  }
}
.ad-slot-placeholder {
  height: 100%;
  background-color: var(--color-gray-100);
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
.dark .ad-slot-placeholder {
  background-color: var(--color-azul);
}
</style>
